import { useEffect, useState } from "react";
import { X, Share, PlusSquare, Smartphone } from "lucide-react";
import { Button } from "./ui/button";

const DISMISS_KEY = "sportly_pwa_install_dismissed_at";
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const IOS_TIP_KEY = "sportly_pwa_ios_tip_dismissed_at";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iPadOS 13+ reports as Mac, so check touch points as well
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
}

function wasRecentlyDismissed(key) {
  const t = Number(localStorage.getItem(key) || 0);
  return t > 0 && Date.now() - t < DISMISS_MS;
}

/** Small unobtrusive install prompt.
 *  - On Chrome/Android/Edge: shows a native "Install app" button via beforeinstallprompt.
 *  - On iOS Safari: shows a manual tip ("tap Share → Add to Home Screen").
 *  - Hides itself when already installed, or when dismissed in the last 14 days.
 */
export const PwaInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosTip, setShowIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    // Chrome / Android / Edge — capture the deferred prompt
    const onBeforeInstall = (e) => {
      e.preventDefault();
      if (wasRecentlyDismissed(DISMISS_KEY)) return;
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari — show manual tip once
    if (isIos() && !wasRecentlyDismissed(IOS_TIP_KEY)) {
      // Small delay so it doesn't fight page load
      const t = setTimeout(() => setShowIosTip(true), 3500);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        clearTimeout(t);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const dismissIosTip = () => {
    setShowIosTip(false);
    localStorage.setItem(IOS_TIP_KEY, String(Date.now()));
  };

  if (visible && deferred) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 bg-slate-900 text-white rounded-lg shadow-xl border border-slate-700 p-4"
        data-testid="pwa-install-prompt"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-slate-800 flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-orange-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">App installeren</p>
            <p className="text-xs text-slate-300 mt-0.5">
              Voeg Sportly toe aan je beginscherm voor snellere toegang.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={handleInstall}
                className="bg-orange-500 hover:bg-orange-600 text-white h-8"
                data-testid="pwa-install-btn"
              >
                Installeer
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={dismiss}
                className="text-slate-400 hover:text-white h-8"
                data-testid="pwa-dismiss-btn"
              >
                Later
              </Button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="text-slate-400 hover:text-white"
            aria-label="Sluiten"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (showIosTip) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 z-50 bg-slate-900 text-white rounded-lg shadow-xl border border-slate-700 p-4"
        data-testid="pwa-ios-tip"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-slate-800 flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-orange-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Voeg Sportly toe aan je beginscherm</p>
            <p className="text-xs text-slate-300 mt-1 leading-relaxed">
              Tik op <Share className="inline w-3.5 h-3.5 -mt-0.5 mx-0.5" /> onderin, kies dan
              <PlusSquare className="inline w-3.5 h-3.5 -mt-0.5 mx-0.5" /> <strong>Zet op beginscherm</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={dismissIosTip}
            className="text-slate-400 hover:text-white shrink-0"
            aria-label="Sluiten"
            data-testid="pwa-ios-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default PwaInstallPrompt;
